import _ from 'lodash';

const getAutomationRule = (automationRules, resourceType) => {
  return automationRules.find((rule) => rule.resourceType === resourceType) || automationRules[0];
};

const getFieldOptions = (field) => {
  return field.options || [];
};

const createDefaultUpdateRule = (field) => {
  if (field.ruleType === 'STRING_EQUAL') {
    return {
      ruleType: 'STRING_EQUAL',
      stringEqualCheck: {
        key: field.key,
        value: field.defaultValue || getFieldOptions(field)[0]?.value || '',
      },
    };
  }

  if (field.ruleType === 'AI_PROMPT') {
    return {
      ruleType: 'AI_PROMPT',
      promptCheck: {
        key: field.key,
        prompt: '',
      },
    };
  }

  return null;
};

const hasFieldOption = (field, value) => {
  return getFieldOptions(field).some((option) => option.value === value);
};

const getHasAtLeastOneVisibleRule = (updateRules, automationRule) => {
  const fieldsVisibleToAdmin = automationRule.fields.filter((field) => Boolean(field.isVisibleToAdmin));
  return updateRules.some((updateRule) => {
    return fieldsVisibleToAdmin.some((field) => {
      if (updateRule.ruleType !== field.ruleType) {
        return false;
      }

      if (updateRule.ruleType === 'STRING_EQUAL') {
        return (
          updateRule.stringEqualCheck.key === field.key &&
          field.options.some((option) => option.value === updateRule.stringEqualCheck.value)
        );
      }

      if (updateRule.ruleType === 'AI_PROMPT') {
        return updateRule.promptCheck.key === field.key;
      }

      return false;
    });
  });
};

const getAreAllRulesRecognized = (updateRules, automationRule) => {
  return updateRules.every((updateRule) => {
    return automationRule.fields.some((field) => {
      if (updateRule.ruleType !== field.ruleType) {
        return false;
      }

      if (updateRule.ruleType === 'STRING_EQUAL') {
        return (
          updateRule.stringEqualCheck.key === field.key &&
          field.options.some((option) => option.value === updateRule.stringEqualCheck.value)
        );
      }

      if (updateRule.ruleType === 'AI_PROMPT') {
        return updateRule.promptCheck.key === field.key;
      }

      return false;
    });
  });
};

const isCustomAutomation = (automationRules, resourceType, updateRules=[]) => {
  const automationRule = automationRules.find((rule) => rule.resourceType === resourceType);

  if (!automationRule) {
    return true;
  }

  const hasAtLeastOneAdminVisibleRule = getHasAtLeastOneVisibleRule(updateRules, automationRule);
  const areAllRulesRecognized = getAreAllRulesRecognized(updateRules, automationRule);
  return !hasAtLeastOneAdminVisibleRule || !areAllRulesRecognized;
};

const getAdminVisibleUpdateRules = (automationRules, resourceType, updateRules) => {
  const visibleFields = getAutomationRule(automationRules, resourceType).fields.filter((field) => Boolean(field.isVisibleToAdmin));

  return _.cloneDeep(updateRules.filter((updateRule) => {
    return visibleFields.some((field) => {
      if (updateRule.ruleType !== field.ruleType) {
        return false;
      }

      if (field.ruleType === 'STRING_EQUAL') {
        return updateRule.stringEqualCheck?.key === field.key;
      }

      if (field.ruleType === 'AI_PROMPT') {
        return updateRule.promptCheck?.key === field.key;
      }

      return false;
    });
  }));
};

const getFieldValueFromUpdateRules = (updateRules, field) => {
  const matchedUpdateRule = (updateRules || []).find((updateRule) => {
    if (updateRule.ruleType !== field.ruleType) {
      return false;
    }

    if (field.ruleType === 'STRING_EQUAL') {
      return updateRule.stringEqualCheck?.key === field.key;
    }

    if (field.ruleType === 'AI_PROMPT') {
      return updateRule.promptCheck?.key === field.key;
    }

    return false;
  });

  if (field.ruleType === 'STRING_EQUAL') {
    if (!hasFieldOption(field, matchedUpdateRule?.stringEqualCheck?.value)) {
      return createDefaultUpdateRule(field)?.stringEqualCheck?.value || '';
    }

    return matchedUpdateRule?.stringEqualCheck?.value || createDefaultUpdateRule(field)?.stringEqualCheck?.value || '';
  }

  if (field.ruleType === 'AI_PROMPT') {
    return matchedUpdateRule?.promptCheck?.prompt || '';
  }

  return '';
};

const updateFieldValueInUpdateRules = (updateRules, field, nextValue) => {
  const nextUpdateRules = _.cloneDeep(updateRules || []);
  const existingIndex = nextUpdateRules.findIndex((updateRule) => {
    if (updateRule.ruleType !== field.ruleType) {
      return false;
    }

    if (field.ruleType === 'STRING_EQUAL') {
      return updateRule.stringEqualCheck?.key === field.key;
    }

    if (field.ruleType === 'AI_PROMPT') {
      return updateRule.promptCheck?.key === field.key;
    }

    return false;
  });

  const nextUpdateRule = existingIndex >= 0 ?
    nextUpdateRules[existingIndex] :
    createDefaultUpdateRule(field);

  if (field.ruleType === 'STRING_EQUAL') {
    nextUpdateRule.stringEqualCheck.value = nextValue;
  }

  if (field.ruleType === 'AI_PROMPT') {
    nextUpdateRule.promptCheck.prompt = nextValue;
  }

  if (existingIndex >= 0) {
    nextUpdateRules[existingIndex] = nextUpdateRule;
  } else {
    nextUpdateRules.push(nextUpdateRule);
  }

  return nextUpdateRules;
};

const buildDefaultVisibleUpdateRules = (automationRules, resourceType) => {
  return getAutomationRule(automationRules, resourceType).fields.filter((field) => {
    return Boolean(field.isRequired) && Boolean(field.isVisibleToAdmin);
  }).map((field) => createDefaultUpdateRule(field));
};

const buildSubmissionUpdateRules = (automationRules, resourceType, updateRules, isTriggerLocked) => {
  if (isTriggerLocked) {
    return updateRules;
  }

  const nextUpdateRules = _.cloneDeep(updateRules);
  const hiddenRequiredFields = getAutomationRule(automationRules, resourceType).fields.filter((field) => {
    return Boolean(field.isRequired) && !Boolean(field.isVisibleToAdmin);
  });

  hiddenRequiredFields.forEach((field) => {
    nextUpdateRules.push(createDefaultUpdateRule(field));
  });

  return nextUpdateRules;
};

const buildAutomationDraft = (automationRules, currentAutomation) => {
  if (!currentAutomation) {
    const defaultResourceType = automationRules[0].resourceType;

    return {
      resourceType: defaultResourceType,
      title: '',
      description: {
        blob: '',
        editorType: 'TEXT',
      },
      groupName: '',
      updateRules: [],
      prompt: '',
      assigneeRns: [],
    };
  }

  const resourceType = currentAutomation.resourceType;
  const shouldPreserveTrigger = isCustomAutomation(automationRules, resourceType, currentAutomation.updateRules);

  return {
    resourceType,
    title: currentAutomation.title,
    description: {
      blob: currentAutomation.description.blob,
      editorType: 'TEXT',
    },
    groupName: currentAutomation.groupName,
    updateRules: shouldPreserveTrigger ?
      currentAutomation.updateRules :
      getAdminVisibleUpdateRules(automationRules, resourceType, currentAutomation.updateRules),
    prompt: currentAutomation.prompt,
    assigneeRns: currentAutomation.assigneeRns || [],
  };
};

const addUpdateRulesWithValueAny = (automationRules, rawAutomation) => {
  const automationRule = automationRules.find((rule) => rule.resourceType === rawAutomation.resourceType);
  const rawUpdateRules = rawAutomation.updateRules || [];

  if (!automationRule) {
    return rawAutomation;
  }

  const stringEqualKeysFromConfig = automationRule.fields
      .filter((field) => field.ruleType === 'STRING_EQUAL' && field.isRequired)
      .map((field) => field.key);
  const stringEqualKeysFromRawAutomation = rawUpdateRules
      .filter((updateRule) => updateRule.ruleType === 'STRING_EQUAL')
      .map((updateRule) => updateRule.stringEqualCheck.key);
  const missingStringEqualKeys = stringEqualKeysFromConfig.filter((key) => {
    return !stringEqualKeysFromRawAutomation.includes(key);
  });

  if (missingStringEqualKeys.length === 0) {
    return rawAutomation;
  }

  return {
    ...rawAutomation,
    updateRules: [
      ...rawUpdateRules,
      ...missingStringEqualKeys.map((key) => ({
        ruleType: 'STRING_EQUAL',
        stringEqualCheck: {
          key,
          value: 'ANY',
        },
      })),
    ],
  };
};

const removeUpdateRulesWithValueAny = (automation) => {
  const updateRules = automation.updateRules;

  return {
    ...automation,
    updateRules: updateRules.filter((updateRule) => {
      return (
        updateRule.ruleType !== 'STRING_EQUAL' ||
        updateRule.stringEqualCheck.value !== 'ANY'
      );
    }),
  };
};

export {
  buildAutomationDraft,
  addUpdateRulesWithValueAny,
  removeUpdateRulesWithValueAny,
  isCustomAutomation,
  getAutomationRule,
  getFieldValueFromUpdateRules,
  getAdminVisibleUpdateRules,
  buildDefaultVisibleUpdateRules,
  buildSubmissionUpdateRules,
  updateFieldValueInUpdateRules,
};
